/**
 * CustomerEmployeeSelector Component
 *
 * Selection tool for customers and employees in soft booking mode.
 * Implements mutually exclusive selection: selecting customers clears employees and vice versa.
 *
 * Features:
 * - Customer dropdown (multi-select, with "add new" for admins)
 * - Employee dropdown (multi-select)
 * - Mutually exclusive selection (matches Vue behavior)
 * - Default logged-in user selection
 * - Selection hint using PointingHint.Anchored component
 */

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useAtomValue } from "jotai";
import { Select } from "@/components/ui/forms/Select";
import type { SelectOption } from "@/components/ui/forms/Select/types";
import { PointingHint } from "@/components/ui/feedback/PointingHint";
import { panelCollapsedAtom } from "@/store/bookingStore";
import { useEmployees } from "@/hooks/data/useEmployees";
import { useCustomers } from "@/hooks/data/useCustomers";
import { useUser, ROLE_ADMIN } from "@/hooks/auth/useUser";
import type { IServiceEmployee, IServiceCustomer } from "@/services/types";
import type { CustomerEmployeeSelectorProps } from "./types";
import {
  toCustomerOptionsWithData,
  toEmployeeOptionsWithData,
} from "@/utils/selectOptions";
import classNames from "classnames";
import styles from "./CustomerEmployeeSelector.module.css";

export const CustomerEmployeeSelector: React.FC<
  CustomerEmployeeSelectorProps
> = ({
  defaultLoggedIn = true,
  selectedEmployees = [],
  selectedCustomers = [],
  horizontal = false,
  multipleEmployees = true,
  multipleCustomers = true,
  employeeOnly = false,
  onSelectEmployees,
  onSelectCustomers,
  className,
  size = "md",
  "data-testid": testId,
}) => {
  // Data hooks
  const {
    employees,
    bookableEmployees,
    isLoading: employeesLoading,
  } = useEmployees();
  const {
    activeCustomers,
    isLoading: customersLoading,
    createCustomer,
    isCreating,
  } = useCustomers();
  const { user, roleLevel } = useUser();

  // Get panel collapsed state - hide hint when panel is collapsed
  const panelCollapsed = useAtomValue(panelCollapsedAtom);

  // Track if we've done initial selection
  const [initialSelectionDone, setInitialSelectionDone] = useState(false);

  // ============ DERIVED DATA ============

  // Determine if hint should be visible (also hide when panel is collapsed)
  const shouldShowHint =
    !panelCollapsed &&
    selectedCustomers.length === 0 &&
    selectedEmployees.length === 0;

  // Customer options for dropdown
  const customerOptions = useMemo(
    () => toCustomerOptionsWithData(activeCustomers),
    [activeCustomers],
  );

  // Employee options for dropdown
  const employeeOptions = useMemo(
    () => toEmployeeOptionsWithData(bookableEmployees),
    [bookableEmployees],
  );

  // Map selected customers to dropdown options
  const selectedCustomerOptions = useMemo(
    () => toCustomerOptionsWithData(selectedCustomers, false),
    [selectedCustomers],
  );

  // Map selected employees to dropdown options
  const selectedEmployeeOptions = useMemo(
    () => toEmployeeOptionsWithData(selectedEmployees, false),
    [selectedEmployees],
  );

  // Customer placeholder text
  const customerPlaceholder = customersLoading
    ? "Laster kunder..."
    : "Velg kunde";

  // ============ HANDLERS ============

  // Handle customer selection - clear employees when customer selected
  const handleCustomerChange = useCallback(
    (selected: SelectOption<IServiceCustomer>[]) => {
      const customers = selected
        .map((opt) => opt.data)
        .filter((c): c is IServiceCustomer => c !== undefined);

      // Clear employees when customers are selected (mutually exclusive - Vue behavior)
      if (customers.length > 0) {
        onSelectEmployees?.([]);
      }

      onSelectCustomers?.(customers);
    },
    [onSelectCustomers, onSelectEmployees],
  );

  // Handle employee selection - clear customers when employee selected
  const handleEmployeeChange = useCallback(
    (selected: SelectOption<IServiceEmployee>[]) => {
      const emps = selected
        .map((opt) => opt.data)
        .filter((e): e is IServiceEmployee => e !== undefined);

      // Clear customers when employees are selected (mutually exclusive - Vue behavior)
      if (emps.length > 0) {
        onSelectCustomers?.([]);
      }

      onSelectEmployees?.(emps);
    },
    [onSelectEmployees, onSelectCustomers],
  );

  // Handle adding a new customer (admin only)
  const handleAddNewCustomer = useCallback(
    async (label: string) => {
      const result = await createCustomer(label);

      if (result) {
        // Find the newly created customer and select it
        const match = result.find(
          (c) => c.name.toLowerCase() === label.toLowerCase(),
        );

        if (match) {
          const createdOption: SelectOption<IServiceCustomer> = {
            id: match.id,
            label: match.name,
            data: match,
          };
          handleCustomerChange([createdOption]);
        }
      }
    },
    [createCustomer, handleCustomerChange],
  );

  // ============ EFFECTS ============

  // Default selection: select logged-in user on first load
  useEffect(() => {
    if (
      !initialSelectionDone &&
      !employeesLoading &&
      employees.length > 0 &&
      defaultLoggedIn &&
      user &&
      selectedEmployees.length === 0 &&
      selectedCustomers.length === 0
    ) {
      const loginEmail = user.email?.toLowerCase();
      if (loginEmail) {
        const currentUser = employees.find(
          (e) =>
            e.settings?.is_bookable && e.email?.toLowerCase() === loginEmail,
        );
        if (currentUser) {
          onSelectEmployees?.([currentUser]);
          onSelectCustomers?.([]);
        }
      }
      setInitialSelectionDone(true);
    }
  }, [
    initialSelectionDone,
    employeesLoading,
    employees,
    defaultLoggedIn,
    user,
    selectedEmployees.length,
    selectedCustomers.length,
    onSelectEmployees,
    onSelectCustomers,
  ]);

  return (
    <div
      className={classNames(
        styles.container,
        horizontal && styles.horizontal,
        className,
      )}
      data-testid={testId}
    >
      {/* Customer dropdown with pointing hint when nothing selected */}
      {!employeeOnly && (
        <PointingHint.Anchored
          direction="right"
          offset={56}
          noFadeOut
          visible={shouldShowHint}
          data-testid="selection-hint"
          target={
            <Select<IServiceCustomer>
              label="Kunde"
              placeholder={customerPlaceholder}
              options={customerOptions}
              value={selectedCustomerOptions}
              multiple={multipleCustomers}
              sorted
              allowAddNew={roleLevel <= ROLE_ADMIN}
              onAddNew={handleAddNewCustomer}
              disabled={customersLoading || isCreating}
              onChange={handleCustomerChange}
              size={size}
              renderOption={(option, state) => (
                <div
                  className={styles.listItem}
                  data-selected={state.selected}
                  data-disabled={state.disabled}
                  style={{ textTransform: "none" }}
                >
                  {option.label}
                </div>
              )}
              data-testid="filter-customer"
            />
          }
        >
          Velg kunde eller ansatt for å finne bookinger.
        </PointingHint.Anchored>
      )}

      {/* Employee dropdown */}
      <Select<IServiceEmployee>
        label="Ansatt"
        placeholder="Velg ansatt"
        options={employeeOptions}
        value={selectedEmployeeOptions}
        multiple={multipleEmployees}
        sorted
        disabled={employeesLoading}
        onChange={handleEmployeeChange}
        size={size}
        renderOption={(option, state) => (
          <div
            className={styles.listItem}
            data-selected={state.selected}
            data-disabled={state.disabled}
            style={{ textTransform: "none" }}
          >
            {option.label}
          </div>
        )}
        data-testid="filter-employee"
      />
    </div>
  );
};

CustomerEmployeeSelector.displayName = "CustomerEmployeeSelector";

export default CustomerEmployeeSelector;
